/**
 * StreamingPicksPreview - Shows parlays as they arrive from the SSE stream
 * using the same ParlayCard component as the final results view.
 *
 * Cards are fully interactive during streaming: users can track parlays,
 * view scores, expand analysis, etc. A subtle pulsing shimmer on each card
 * indicates it arrived during the current streaming session.
 *
 * When no parlays have arrived yet, a premium empty state is shown with an
 * orbital animation, phase progress stepper, and skeleton placeholder cards.
 */

import type { ParlayScore } from '@hooks/useStrategyStats';
import type { GeneratedParlay } from '@parlay-picker/shared/api/picksApi';
import type { BestOddsResult } from '@parlay-picker/shared/oddsUtils';
import { createParlayKey, type LegSignatureInput } from '@parlay-picker/shared/parlaySignature';
import type { PickStreamPhase } from '@parlay-picker/shared/pickStreamEvents';
import {
  type ParlayScoreData,
  useCurrentPhase,
  useGenerationStore,
  usePhaseMessage,
  useStreamingParlays,
  useStreamingScores,
} from '@stores/generationStore';
import { cn } from '@utils/cn';
import { AnimatePresence, motion } from 'framer-motion';
import { BarChart3, Brain, Check, FileText, Sparkles, TrendingUp } from 'lucide-react';
import type { ReactElement } from 'react';
import type { RiskTier } from '../constants';
import { RISK_TIERS } from '../constants';
import type { Game } from '../utils';
import type { ChartClickData } from './ParlayCard';
import { ParlayCard } from './ParlayCard';

// =============================================================================
// Helpers
// =============================================================================

/** Derive a RiskTier from a parlay's name (e.g. "Safe Play #1" → safe tier). */
function deriveTierFromParlay(parlay: GeneratedParlay): RiskTier {
  const name = (parlay.name ?? '').toLowerCase();
  for (const tier of RISK_TIERS) {
    if (name.includes(tier.key)) return tier;
  }
  // Fallback to value tier
  return RISK_TIERS.find((t) => t.key === 'value') ?? RISK_TIERS[0];
}

// =============================================================================
// Empty State Constants
// =============================================================================

const PHASE_STEPS: Array<{
  phase: PickStreamPhase;
  label: string;
  icon: typeof BarChart3;
}> = [
  { phase: 'fetching_data', label: 'Fetching Odds', icon: BarChart3 },
  { phase: 'loading_stats', label: 'Loading Stats', icon: TrendingUp },
  { phase: 'building_prompt', label: 'Building Analysis', icon: FileText },
  { phase: 'generating', label: 'AI Generating', icon: Brain },
  { phase: 'finalizing', label: 'Scoring & Tiers', icon: Sparkles },
];

const PHASE_ORDER: PickStreamPhase[] = [
  'fetching_data',
  'loading_stats',
  'building_prompt',
  'generating',
  'finalizing',
];

// =============================================================================
// StreamingEmptyState — premium loading experience
// =============================================================================

function StreamingEmptyState({
  currentPhase,
  phaseMessage,
}: {
  currentPhase: PickStreamPhase;
  phaseMessage: string | null;
}): ReactElement {
  const currentIdx = PHASE_ORDER.indexOf(currentPhase);

  return (
    <div className="flex flex-col items-center py-4 px-4">
      {/* ---- Orbital Animation ---- */}
      <div className="relative w-32 h-32 mb-8">
        {/* Outer dashed ring — slow rotation */}
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
          className="absolute inset-0 rounded-full border-2 border-dashed border-theme-primary/20"
        />

        {/* Middle ring — breathing opacity */}
        <motion.div
          animate={{ opacity: [0.2, 0.5, 0.2] }}
          transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute inset-4 rounded-full border border-theme-primary/30"
        />

        {/* Inner glow — pulsing scale */}
        <motion.div
          animate={{ scale: [0.9, 1.1, 0.9] }}
          transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute inset-8 rounded-full bg-theme-primary/10"
        />

        {/* Center dot */}
        <motion.div
          animate={{ scale: [0.85, 1.15, 0.85] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute inset-0 flex items-center justify-center"
        >
          <div className="w-5 h-5 rounded-full bg-theme-primary shadow-lg shadow-theme-primary/40" />
        </motion.div>

        {/* Orbiting dot 1 — primary, 6s */}
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 6, repeat: Infinity, ease: 'linear' }}
          className="absolute inset-0"
        >
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-theme-primary shadow-sm shadow-theme-primary/50" />
        </motion.div>

        {/* Orbiting dot 2 — success, 9s, offset start */}
        <motion.div
          animate={{ rotate: 360 }}
          transition={{
            duration: 9,
            repeat: Infinity,
            ease: 'linear',
            delay: 2,
          }}
          className="absolute inset-0"
        >
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-theme-success shadow-sm shadow-theme-success/50" />
        </motion.div>

        {/* Orbiting dot 3 — warning, counter-clockwise, inner ring */}
        <motion.div
          animate={{ rotate: -360 }}
          transition={{ duration: 12, repeat: Infinity, ease: 'linear' }}
          className="absolute inset-4"
        >
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-theme-warning shadow-sm shadow-theme-warning/50" />
        </motion.div>
      </div>

      {/* ---- Title + animated phase message ---- */}
      <h3 className="text-lg font-semibold text-theme-text mb-2">Generating Your Picks</h3>
      <div className="h-6 mb-8">
        <AnimatePresence mode="wait">
          <motion.p
            key={phaseMessage ?? 'default'}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.25 }}
            className="text-sm text-theme-text-secondary text-center"
          >
            {phaseMessage || 'Preparing your analysis...'}
          </motion.p>
        </AnimatePresence>
      </div>

      {/* ---- Phase Progress Stepper ---- */}
      <div className="bg-theme-surface/50 border border-theme-border/50 rounded-xl p-4 mb-8 w-full max-w-xs">
        <div className="space-y-3">
          {PHASE_STEPS.map((step) => {
            const stepIdx = PHASE_ORDER.indexOf(step.phase);
            const isComplete = stepIdx < currentIdx;
            const isCurrent = stepIdx === currentIdx;
            const Icon = step.icon;

            return (
              <div key={step.phase} className="flex items-center gap-3">
                <div
                  className={cn(
                    'w-7 h-7 rounded-full flex items-center justify-center shrink-0 transition-all duration-300',
                    isComplete && 'bg-theme-success/20',
                    isCurrent && 'bg-theme-primary/20',
                    !isComplete && !isCurrent && 'bg-theme-surface opacity-40',
                  )}
                >
                  {isComplete ? (
                    <Check className="w-3.5 h-3.5 text-theme-success" />
                  ) : (
                    <motion.div
                      animate={isCurrent ? { opacity: [0.5, 1, 0.5] } : undefined}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    >
                      <Icon
                        className={cn(
                          'w-3.5 h-3.5',
                          isCurrent ? 'text-theme-primary' : 'text-theme-text-tertiary',
                        )}
                      />
                    </motion.div>
                  )}
                </div>
                <span
                  className={cn(
                    'text-sm font-medium transition-colors duration-300',
                    isComplete && 'text-theme-success',
                    isCurrent && 'text-theme-text',
                    !isComplete && !isCurrent && 'text-theme-text-tertiary/50',
                  )}
                >
                  {step.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Props
// =============================================================================

export interface StreamingPicksPreviewProps {
  games: Game[];
  onTrack: (rec: GeneratedParlay, tierName: string) => Promise<void>;
  isParlayTracked: (rec: GeneratedParlay) => boolean;
  trackingKeys: Set<string>;
  onChartClick: (data: ChartClickData) => void;
  showDetailedAnalysis: boolean;
  oddsComparisons: Map<string, BestOddsResult>;
  onScoreRefresh: () => Promise<void>;
}

// =============================================================================
// Component
// =============================================================================

export function StreamingPicksPreview({
  games,
  onTrack,
  isParlayTracked,
  trackingKeys,
  onChartClick,
  showDetailedAnalysis,
  oddsComparisons,
  onScoreRefresh,
}: StreamingPicksPreviewProps): ReactElement | null {
  const streamingParlays = useStreamingParlays();
  const streamingScores = useStreamingScores();
  const currentPhase = useCurrentPhase();
  const phaseMessage = usePhaseMessage();

  const isGenerating = useGenerationStore((s) => s.isGenerating);

  // Show nothing only when not generating at all
  if (streamingParlays.length === 0 && !currentPhase && !isGenerating) return null;

  const getParlayTrackingKey = (parlay: GeneratedParlay): string =>
    createParlayKey(
      (parlay.legs ?? []).map(
        (leg): LegSignatureInput => ({
          player: leg.player || leg.team || '',
          statType: leg.statType || '',
          line: leg.line,
          overUnder: leg.overUnder,
          gameDate: leg.gameDate,
          leagueKey: leg.leagueKey,
        }),
      ),
    );

  return (
    <div className="space-y-3">
      {/* Empty state — premium loading experience before any cards arrive */}
      <AnimatePresence mode="wait">
        {streamingParlays.length === 0 && (currentPhase || isGenerating) && (
          <motion.div
            key="streaming-empty-state"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, scale: 0.95, y: -20 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
          >
            <StreamingEmptyState
              currentPhase={currentPhase ?? 'building_prompt'}
              phaseMessage={phaseMessage ?? 'Preparing your analysis...'}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Status line — shows once cards start arriving */}
      {streamingParlays.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-2 px-1"
        >
          <div className="w-2 h-2 rounded-full bg-theme-primary animate-pulse" />
          <span className="text-sm font-medium text-theme-text-secondary">
            {`${streamingParlays.length} parlay${streamingParlays.length !== 1 ? 's' : ''} generated so far...`}
          </span>
        </motion.div>
      )}

      {/* Phase message during card streaming */}
      {streamingParlays.length > 0 && phaseMessage && (
        <p className="text-xs text-theme-text-tertiary px-1">{phaseMessage}</p>
      )}

      {/* Streaming cards — same ParlayCard as final results */}
      <AnimatePresence mode="popLayout">
        {streamingParlays.map((parlay: GeneratedParlay, i: number) => {
          const tier = deriveTierFromParlay(parlay);
          const scores: ParlayScoreData | undefined = streamingScores[i];
          const dynamicScore: ParlayScore | null = scores?.strategyScore ?? null;
          const predictionScore: ParlayScore | null = scores?.predictionScore ?? null;

          return (
            <motion.div
              key={`streaming-${parlay.rank ?? i}`}
              initial={{ opacity: 0, y: 16 }}
              animate={{
                opacity: 1,
                y: 0,
                boxShadow: [
                  '0 0 0 1px rgba(99, 102, 241, 0.1)',
                  '0 0 0 1px rgba(99, 102, 241, 0.3)',
                  '0 0 0 1px rgba(99, 102, 241, 0.1)',
                ],
              }}
              transition={{
                opacity: { duration: 0.35, ease: 'easeOut' },
                y: { duration: 0.35, ease: 'easeOut' },
                boxShadow: {
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut',
                },
              }}
              className="rounded-xl"
            >
              <ParlayCard
                rec={parlay}
                index={i}
                tier={tier}
                leagueKey={parlay.leagueKey}
                games={games}
                isTracked={isParlayTracked(parlay)}
                isTracking={trackingKeys.has(getParlayTrackingKey(parlay))}
                onTrack={onTrack}
                onChartClick={onChartClick}
                showDetailedAnalysis={showDetailedAnalysis}
                dynamicScore={dynamicScore}
                predictionScore={predictionScore}
                oddsComparisons={oddsComparisons}
                onScoreRefresh={onScoreRefresh}
                isStreaming
              />
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}
